import { ApplicationTypes, Model } from 'jms-domain'
import { WallType } from 'jms-domain/src/__generated/model/index.js'
import { useObservableEagerState } from 'observable-hooks'

import { SceneEntityDimensionIndicators } from '@salsita-ai/platform-project-sdk/configurator'
import { type DomainEntityId } from '@salsita-ai/platform-project-sdk/domain'

import { Floor } from '../canvas/Floor.js'
import { Wall } from '../canvas/Wall.js'
import { configuratorInterface } from '../systems/configurator-interface'

export interface RoomObjectProps {
  id: DomainEntityId<'Room'>
  wallIds: Array<DomainEntityId<'Wall'>>
}

const extraLength = {
  [Model.RoomShapeType.I]: { [WallType.A]: 0, [WallType.B]: 0, [WallType.C]: 0 },
  [Model.RoomShapeType.L]: { [WallType.A]: 1, [WallType.B]: 0, [WallType.C]: 0 },
  [Model.RoomShapeType.U]: { [WallType.A]: 1, [WallType.B]: 0, [WallType.C]: 1 },
} as const

const wallPosition = {
  [Model.RoomShapeType.I]: { [WallType.A]: 0, [WallType.B]: 0, [WallType.C]: 0 },
  [Model.RoomShapeType.L]: { [WallType.A]: 0, [WallType.B]: 0, [WallType.C]: 0 },
  [Model.RoomShapeType.U]: { [WallType.A]: 0, [WallType.B]: 0, [WallType.C]: -1 },
} as const

export const RoomObject = (props: RoomObjectProps) => {
  const { id, wallIds } = props

  const { navigationSystem, domain } = configuratorInterface.useDomain()

  const state = configuratorInterface.useRenderedState()
  const $room = domain.get$Entity(id, state)
  const $walls = domain.get$Entities(wallIds, state)

  const activeNavigationState = useObservableEagerState(navigationSystem.activeNavigationState$)
  const showAllWalls = activeNavigationState === ApplicationTypes.NavigationState.Room
  const isFinalizingMainImage = activeNavigationState === ApplicationTypes.NavigationState.FinalizeGlamour

  return (
    <>
      <SceneEntityDimensionIndicators id={id} />
      <Floor />
      {!isFinalizingMainImage &&
        $walls.map(wall => {
          return (
            <group
              visible={wall.$active}
              key={wall.id}
              position={[
                // Push the wall one wall-length to the left if needed to fill corner.
                wall.$wallObjectX,
                0,
                wall.$wallObjectZ - (wallPosition[$room.shape][wall.wallType] < 0 ? wall.$wallThickness : 0),
              ]}
              rotation={[0, wall.$rotationY, 0]}
            >
              <Wall
                // Push the wall one wall-length to the right if needed to fill corner.
                length={wall.length + (extraLength[$room.shape][wall.wallType] > 0 ? wall.$wallThickness : 0)}
                height={wall.$height}
                hiddenHeight={wall.$minHeight}
                thickness={wall.$wallThickness}
                cameraHiding={!showAllWalls && !isFinalizingMainImage}
              />
            </group>
          )
        })}
    </>
  )
}
